import { toast } from 'sonner'
import { useMe } from '@/features/auth/useMe'
import { useIsArticleFrozen } from '@/features/moderation/useArticleModeration'
import { ARTICLE_SAVE_REFUSED } from '@/features/moderation/lib/reviewInProgress'
import type { Instruction } from '@/features/instructions/types'
import type { IUser } from '@/types/auth'
import type { ArticleForm } from '@/features/instructions/lib/articleDraft'
import { EditorToolbar } from './EditorToolbar'
import { EditorRail, type EditorTabId } from './EditorRail'
import { EditorTabContent } from './EditorTabContent'
import { useEditorActions } from './useEditorActions'
import { useTranslation } from './useTranslation'
import { ArticleReviewLockNotice } from './ArticleReviewLockNotice'
import { useArticleForm } from './useArticleForm'
import { useArticleAutosave } from './useArticleAutosave'
import { messagesNow, useUiLang } from '@/i18n'
import { messages } from './messages'

const getAuthorInfo = (me: IUser | undefined): { name: string; avatar: string | null } => ({
  name: me?.username ?? me?.email?.split('@')[0] ?? messagesNow(messages).author,
  avatar: me?.avatar ?? null,
})

interface Props {
  instruction: Instruction | undefined
  /** Живое + черновик (`seedArticleForm`): от него считаются и `dirty`, и дифф сохранения. */
  seed: ArticleForm
  /** Когда ушли запросы засева — узнать итоговый засев после сохранения (`useArticleForm`). */
  seedReadAt: number | undefined
  isCreate: boolean
  /** Вкладка и язык — у `InstructionEditor`: переживают создание статьи. */
  tab: EditorTabId
  onTabChange: (tab: EditorTabId) => void
  lang: 'ru' | 'en'
  onLangChange: (lang: 'ru' | 'en') => void
}

export const ArticleEditorBody = ({
  instruction,
  seed,
  seedReadAt,
  isCreate,
  tab: activeTab,
  onTabChange: setActiveTab,
  lang: activeLang,
  onLangChange: setActiveLang,
}: Props) => {
  const { form, update, dirty, markSent } = useArticleForm(seed, seedReadAt)

  const { save, remove, saving } = useEditorActions({ isCreate, instruction })
  const { translate, isTranslating, status: translationStatus } = useTranslation(instruction)

  const { data: me } = useMe()
  useUiLang() // подписка: фолбэк имени автора берётся из `messagesNow`
  const { name: authorName, avatar: authorAvatar } = getAuthorInfo(me)

  const articleId = instruction?.id ?? null
  // Проверка `pending` замораживает форму и действия; правка сорвала бы заявку.
  const frozen = useIsArticleFrozen(articleId)
  // Страховка от клика за миг до того, как опрос принёс `pending`.
  const onSave = (silent = false) => {
    if (frozen) return void (silent ? undefined : toast.error(ARTICLE_SAVE_REFUSED))
    const sentForm = form
    void save(sentForm, seed, { silent }).then((saved) =>
      saved ? markSent(sentForm, saved) : undefined,
    )
  }
  useArticleAutosave({
    enabled: !frozen,
    flushOnLeave: !isCreate,
    dirty,
    saving,
    form,
    run: () => onSave(true),
  })

  return (
    <div>
      <div className="max-w-6xl mx-auto px-4 py-10">
        <div className="space-y-6">
          <EditorRail active={activeTab} onChange={setActiveTab} />
          <div className="min-w-0 space-y-6 [&_label]:text-lg [&_label]:font-medium">
            {/* Во «Доступе» о проверке говорит своя карточка — плашка там дублировала бы её. */}
            {activeTab === 'access' ? null : <ArticleReviewLockNotice articleId={articleId} />}
            {/* Язык, перевод и сохранение — прямо на странице текста, над заголовком справа. */}
            {activeTab === 'content' ? (
              <div className="flex justify-end">
                <EditorToolbar
                  activeLang={activeLang}
                  onLangChange={setActiveLang}
                  dirty={dirty}
                  saving={saving}
                  onSave={() => onSave()}
                  instruction={instruction ?? null}
                  onTranslate={() => translate()}
                  isTranslating={isTranslating}
                  translationStatus={translationStatus}
                  frozen={frozen}
                  seed={seed}
                />
              </div>
            ) : null}
            <EditorTabContent
              activeTab={activeTab}
              activeLang={activeLang}
              form={form}
              update={update}
              articleId={articleId}
              frozen={frozen}
              accessType={instruction?.accessType ?? 'private'}
              dirty={dirty}
              slug={instruction?.slug ?? null}
              isCreate={isCreate}
              onDelete={() => remove(true)}
              authorName={authorName}
              authorAvatar={authorAvatar}
              viewsCount={instruction?.viewsCount ?? 0}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
